import Marker3DView from "../../views/marker-3d-view";
import RenderLoop from "../../control-flow/render-loop";
import SectionMarker from "./section-marker";
import Signal from "../../control-flow/signal";
import SingleWallComponent from "./single-wall-component";
import UpdateSceneEnvironment from "../../view3d/update-scene-environment";
import VerticalPanelView from "../../views/vertical-panel-view";
import WallView from "../../views/wall-view";
import directionalPosition from "../../view3d/directional-position";
import getLogger from '../../logging'
import lookAtWall from "../../view3d/look-at-wall";
import makeStandardRenderer from "../../view3d/make-standard-renderer";
import optimizeDirectionalShadow from "../../view3d/optimize-directional-shadow";
import sizeReady from "../../html/size-ready";
import { DefaultLoadingManager, DirectionalLight, Group, Object3D, PerspectiveCamera, Raycaster, Scene, Vector2, Vector3, WebGLRenderer } from "three";
import { DirectionalLightingView } from "../../views/directional-lighting-view";
import { Wall } from "../../domain";
import { WallViewConfig, wallViewDefaults } from "../../views/views-config";
import { clone, OptionalProperties, update } from "../../json/optional-properties";
import { getParentView } from "../../controllers/base-view-controllers";
import { mfac, overrideMaterialFactory } from "../../cps";
import { ndce } from "../../view3d";
import { setupResizeHandler } from "../../view3d/setup-resize-handler";

const log = getLogger('single-wall');

export type SingleWallAppConfig = {
    showMarkers: boolean;
    wallView: WallViewConfig;
    environmentAssetId: string;
}
export type SingleWallAppOptions = OptionalProperties<SingleWallAppConfig>;

const singleWallAppDefaults: SingleWallAppConfig = {
    showMarkers: false,
    wallView: clone(wallViewDefaults),
    environmentAssetId: "environments/studio-01", // "environments/synthetic-01",
};

export type Measurement = {
    elevation?: number;
    difference?: number;
    height?: number;
    depth?: number;
};

export class SingleWallApp {
    wall: Wall;
    view: WallView;
    markers: SectionMarker[] = [];
    config: SingleWallAppConfig = clone(singleWallAppDefaults);
    root: SingleWallComponent;

    scene: Scene = new Scene();
    camera: PerspectiveCamera = new PerspectiveCamera(10, 1, 1, 3000);
    renderer: WebGLRenderer;
    raycaster = new Raycaster();
    measureableObjects: Object3D[] = [];
    lastHitObject: Object3D;
    loop: RenderLoop;

    sceneEnvironment: UpdateSceneEnvironment;
    lightingView: DirectionalLightingView;

    onMeasure = new Signal<[Measurement]>('onMeasure', 'single-wall');

    constructor(wall: Wall, options: SingleWallAppOptions) {
        overrideMaterialFactory();
        update(this.config, options);
        this.wall = wall;
        wall.rotation.y = 0;
        wall.position.x = wall.position.y = 0;

        this.view = new WallView(this.wall, this.config.wallView);
        this.view.onReady.add(this.setupMeasurementHandlers)
        this.view.onReady.add(this.updateMarkers)
        this.setupMarkers();

        this.root = new SingleWallComponent(this.markers);

        this.renderer = makeStandardRenderer(this.root.canvas);
        this.loop = new RenderLoop(this.render);
        setupResizeHandler(
            this.root.canvas, this.camera, this.renderer, this.loop.update);
        mfac.onLoad.add(this.loop.update);

        this.scene.add(this.view);
        this.scene.add(this.camera);

        sizeReady(this.root.canvas, () => {
            const
                canvas = this.root.canvas,
                aspect = canvas.clientWidth / canvas.clientHeight;
            this.camera.aspect = aspect;
            // look at the wall with 10" margin on each side
            lookAtWall(this.camera, this.wall, 10);
            this.loop.update();
        });

        this.sceneEnvironment = new UpdateSceneEnvironment(this.renderer, DefaultLoadingManager);
        this.sceneEnvironment.update(this.scene, this.config.environmentAssetId);

        this.setupMainLight();

        this.loop.update();
        this.loop.start();
    }

    render = () => {
        this.renderer.clear();
        this.renderer.render(this.scene, this.camera);
    };

    setupMarkers() {
        // This assumes that we're only using wall.markers for section markers,
        // if we change that, we'll need a way to distinguish different types
        // of markers, e.g. and `id` prefix or new properties on the Marker
        // domain model class.
        for(const model of this.wall.markers) {
            this.markers.push(new SectionMarker(model.id));
        }
    }

    updateMarkers = () => {
        const views = new Map<string, Marker3DView>();
        this.view.traverse(obj => {
            if(obj instanceof Marker3DView) {
                views.set(obj.model.id, obj);
            }
        });
        const v = new Vector2();
        for(const sm of this.markers) {
            const view = views.get(sm.label);
            if(view) {
                view.toScreen(this.root.canvas, this.camera, v);
                sm.left = v.x;
                sm.top = v.y;
            } else {
                log.error(`Unable to match section marker ID '${sm.label}'`);
            }
        }
    }

    setupMainLight() {
        const
            group = new Group(),
            mainLight = new DirectionalLight();
        this.scene.add(group);
        group.add(mainLight);
        mainLight.castShadow = true;
        mainLight.intensity = 1;
        mainLight.shadow.mapSize.width = 4096;
        mainLight.shadow.mapSize.height = 4096;

        const alt = 0.374, az = 4.515;
        directionalPosition(mainLight, alt, az);
        const sphere = optimizeDirectionalShadow(mainLight, [this.wall]);
        group.position.copy(sphere.center);
        mainLight.position.sub(sphere.center);
    }

    setupMeasurementHandlers = () => {
        this.root.canvas.addEventListener('click', this.handleMeasurementClick);
        
        this.view.traverse((obj: Object3D) => {
            if(isHorizontalObject(obj) || isVerticalPanel(obj))
            {
                this.measureableObjects.push(obj);
            }
        });
    }

    handleMeasurementClick = (event: MouseEvent) => {
        this.raycaster.setFromCamera(ndce(event), this.camera);
        const hits = this.raycaster.intersectObjects(this.measureableObjects);
        // This looks like a loop, but we'll break on the first hit, which is
        // the one closest to the camera.
        for(const hit of hits) {
            if(isHorizontalObject(hit.object)) {
                if(this.lastHitObject && isHorizontalObject(this.lastHitObject)) {
                    const
                        first = elevation(this.lastHitObject),
                        second = elevation(hit.object);
                    this.lastHitObject = null;
                    this.onMeasure.trigger({
                        elevation: second,
                        difference: Math.abs(first - second),
                    });
                } else {
                    this.lastHitObject = hit.object;
                    this.onMeasure.trigger({
                        elevation: elevation(hit.object),
                    });
                }
            }
            else if(isVerticalPanel(hit.object)) {
                this.lastHitObject = hit.object;
                    this.onMeasure.trigger(getHeightAndDepth(hit.object));
            }
            break;
        }
    }
}
export default SingleWallApp;

// Below are a few functions to support the click-to-measure behavior on the
// build page.

const scratch = new Vector3();
const elevation = (leaf: Object3D): number => {
    leaf.getWorldPosition(scratch);
    return scratch.y;
};


const getHeightAndDepth = (obj: Object3D): Measurement => {
    const view = getParentView(obj);
    if(view && view instanceof VerticalPanelView) {
        return {
            depth: view.model.depth,
            height: view.model.height,
        }
    }
};

const isVerticalPanel = (leaf: Object3D) => {
    let obj = leaf;
    while(obj != null) {
        if(obj instanceof VerticalPanelView) {
            return true;
        }
        if(obj.name && obj.name.toLowerCase().startsWith('vert')) {
            return true;
        }
        obj = obj.parent;
    }
    return false;
}

const isHorizontalObject = (leaf: Object3D) => {
    let obj = leaf;
    while(obj != null) {
        if(obj.name.includes("Fixed")
           || obj.name.includes("Adj")
           || obj.name.includes("Rod")
           || obj.name.includes("Slanted"))
        {
            return true;
        }
        obj = obj.parent;
    }
    return false;
}
